import type { BEWSBalanceModel, BalanceUpdate } from '@exchange/libs/balances/service/src';
import type { AccountRatioStatus } from '@exchange/libs/futures/service/src/lib/account-ratio';
import type { BEOrderModel } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import type { OrderTradesPair } from '@exchange/libs/order/shared-model/src/lib/order-model';
import type { BEFeeModel, BETradeModel } from '@exchange/libs/trade/trade-history/service/src';
import type { wsSportChannelNames, WSIncomingEventTypes, WSIncomingBasicMessagePayload } from '@exchange/libs/utils/wss/src/lib/websocket-spot-model';

export enum FundingUpdateType {
  FUNDS_DEPOSITED = 'FUNDS_DEPOSITED',
  FUNDS_WITHDRAWN = 'FUNDS_WITHDRAWN',
}

export enum TradingUpdateType {
  ORDER_CREATED = 'ORDER_CREATED',
  OPEN = 'OPEN',
  ORDER_REJECTED = 'ORDER_REJECTED',
  ORDER_FULLY_FILLED = 'ORDER_FULLY_FILLED',
  TRADE_SETTLED = 'TRADE_SETTLED',
  ORDER_CLOSED = 'ORDER_CLOSED',
  STOP_ORDER_TRIGGERED = 'STOP_ORDER_TRIGGERED',
  SETTLEMENT = 'SETTLEMENT',
}

export enum AccountUpdateType {
  TRADING_SETTINGS_CHANGED = 'TRADING_SETTINGS_CHANGED',
}

export interface AccountHistoryMessage extends WSIncomingBasicMessagePayload {
  type:
    | WSIncomingEventTypes.ACCOUNT_UPDATE
    | WSIncomingEventTypes.ACTIVE_ORDERS_SNAPSHOT
    | WSIncomingEventTypes.INACTIVE_ORDERS_SNAPSHOT
    | WSIncomingEventTypes.BALANCES_SNAPSHOT
    | WSIncomingEventTypes.MARGIN_UPDATE
    | WSIncomingEventTypes.OPEN_POSITIONS_SNAPSHOT;
}

/* eslint-disable camelcase */
export interface OrderSnapshot extends AccountHistoryMessage {
  channel_name: typeof wsSportChannelNames.ACCOUNT_HISTORY;
  type: WSIncomingEventTypes.ACTIVE_ORDERS_SNAPSHOT | WSIncomingEventTypes.INACTIVE_ORDERS_SNAPSHOT;
  orders: Array<OrderTradesPair>;
}

export interface ActiveOrderSnapshot extends OrderSnapshot {
  type: WSIncomingEventTypes.ACTIVE_ORDERS_SNAPSHOT;
}

export interface InactiveOrderSnapshot extends OrderSnapshot {
  type: WSIncomingEventTypes.INACTIVE_ORDERS_SNAPSHOT;
}

export interface NormalizedPerpsPosition {
  instrument_code: string;
  direction: 'LONG' | 'SHORT';
  open_volume: string;
  open_position: string;
  realised_pnl: string;
  average_entry_price: string;
  last_update_time: string;
  // We won't have these if the position has not had a settlement yet
  last_settlement?: string;
  mark_price?: string;
}

// Initial snapshot
export interface PerpsPosition {
  instrument_code: string;
  direction: 'LONG' | 'SHORT';
  open_volume: string;
  open_position: string;
  realised_pnl: string;
  settlement_currency: string;
  // We won't have these if the position has not had a settlement yet
  last_settlement: string | null;
  last_settlement_timestamp: string | null;
  mark_price: string | null;
}

export interface PerpsPositionUpdate {
  instrument_code: string;
  direction: 'LONG' | 'SHORT';
  open_position: string;
  open_volume: string;
  margin_used: string;
}

export interface PerpsSettlement {
  instrument_code: string;
  direction: 'LONG' | 'SHORT';
  open_position: string;
  open_volume: string;
  pending_buy_size: string;
  pending_sell_size: string;
  profit: string;
  total_profit: string;
  account_ratio: string;
  maintenance_margin: string;
  margin_used: string;
  mark_price: string;
  time: string;
  time_nano: number;
  quote_currency_balance: string;
}

export interface OpenPositionsSnapshot extends AccountHistoryMessage {
  type: WSIncomingEventTypes.OPEN_POSITIONS_SNAPSHOT;
  account_ratio: string | null;
  maintenance_margin: string;
  margin_used: string;
  positions: Array<PerpsPosition>;
}

export interface BalancesSnapshot extends AccountHistoryMessage {
  channel_name: typeof wsSportChannelNames.ACCOUNT_HISTORY;
  type: WSIncomingEventTypes.BALANCES_SNAPSHOT;
  account_id: string;
  balances: Array<BEWSBalanceModel>;
}

export interface AccountUpdateMessage extends AccountHistoryMessage {
  channel_name: typeof wsSportChannelNames.ACCOUNT_HISTORY;
  type: WSIncomingEventTypes.ACCOUNT_UPDATE;
  account_id: string;
  update: AccountUpdate;
}

export enum AccountUpdateActivity {
  FUNDING = 'FUNDING',
  TRADING = 'TRADING',
  ACCOUNT = 'ACCOUNT',
}
export interface AccountUpdate {
  id: string;
  activity: AccountUpdateActivity;
  type: FundingUpdateType | TradingUpdateType | AccountUpdateType;
  time: string;
  sequence: number;
  account_id: string;
}

export interface WSSFundingUpdate extends AccountUpdate {
  activity: AccountUpdateActivity.FUNDING;
  type: FundingUpdateType;
  account_id: string;
  amount: string;
  currency_code: string;
  balances: Array<BalanceUpdate>;
}

export interface WSMarginUpdate extends AccountHistoryMessage {
  cash_balance: string;
  estimated_profit: string;
  maintenance_margin: string;
  margin_used: string;
  ratio: number;
  status: AccountRatioStatus;
  type: WSIncomingEventTypes.MARGIN_UPDATE;
}

export interface AccountTradingBase {
  account_holder: string;
  account_id: string;
  activity: AccountUpdateActivity.TRADING;
  order_id: string;
  sequence: number;
  time: string;
  time_nano: string; // fast only
}

export interface TradingUpdatePerpsBase {
  margin_used: string;
  maintenance_margin: string;
}

interface OrderClosedUpdateBase extends AccountTradingBase {
  balances: Array<BalanceUpdate>;
  filled_amount: string;
  id: string;
  order_book_sequence: number;
  reason: string;
  type: TradingUpdateType.ORDER_CLOSED;
}

interface OrderClosedUpdatePerps extends OrderClosedUpdateBase, TradingUpdatePerpsBase {}

export type OrderClosedUpdate = OrderClosedUpdateBase | OrderClosedUpdatePerps;

interface OrderCreatedUpdateBase extends AccountTradingBase {
  id: string;
  balances: Array<BalanceUpdate>;
  order: BEOrderModel;
  type: TradingUpdateType.ORDER_CREATED;
}

interface OrderCreatedUpdatePerps extends OrderCreatedUpdateBase, TradingUpdatePerpsBase {}

export type OrderCreatedUpdate = OrderCreatedUpdateBase | OrderCreatedUpdatePerps;

export interface OrderBookedUpdate extends AccountTradingBase {
  id: string;
  locked: BalanceUpdate;
  order: BEOrderModel;
  type: TradingUpdateType.OPEN;
}

export interface OrderRejectedUpdate extends AccountTradingBase {
  balances: Array<BalanceUpdate>;
  filled_amount: string;
  id: string;
  reason: string;
  type: TradingUpdateType.ORDER_REJECTED;
}

export interface StopOrderTriggeredUpdate extends AccountTradingBase {
  order_book_sequence: number;
  type: TradingUpdateType.STOP_ORDER_TRIGGERED;
}

interface TradeSettledUpdateBase extends AccountTradingBase {
  amount: string;
  balances: Array<BalanceUpdate>;
  fee: BEFeeModel;
  filled_amount: string;
  id: string;
  order: {
    amount: string;
    filled_amount: string;
  };
  trade: BETradeModel;
  type: TradingUpdateType.TRADE_SETTLED;
}

interface TradeSettledUpdatePerps extends TradeSettledUpdateBase, TradingUpdatePerpsBase {
  position: PerpsPositionUpdate;
}

export type TradeSettledUpdate = TradeSettledUpdateBase | TradeSettledUpdatePerps;

export interface OrderFullyFilledUpdate extends AccountTradingBase {
  order: {
    amount: string;
    filled_amount: string;
  };
  type: TradingUpdateType.ORDER_FULLY_FILLED;
}

export interface TradeSettlementUpdate extends Omit<AccountTradingBase, 'time_nano'>, PerpsSettlement {
  type: TradingUpdateType.SETTLEMENT;
}
/* eslint-enable camelcase */

export type WSSTradingUpdate =
  | OrderClosedUpdate
  | OrderCreatedUpdate
  | OrderBookedUpdate
  | OrderFullyFilledUpdate
  | OrderRejectedUpdate
  | StopOrderTriggeredUpdate
  | TradeSettledUpdate
  | TradeSettlementUpdate;

export type WSTradingUpdatePerps = OrderClosedUpdatePerps | OrderCreatedUpdatePerps | TradeSettledUpdatePerps | TradeSettlementUpdate;

export const isPerpsTradingUpdate = <T extends TradingUpdatePerpsBase>(message: unknown): message is T =>
  typeof message === 'object' && message !== null && 'margin_used' in message && 'maintenance_margin' in message;
